import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PlacesAutocomplete, { geocodeByPlaceId } from "../PlacesAutocomplete/PlacesAutocomplete";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { auth } from '../../firebase/firebaseConfig';
import InputForm from "../InputForm/InputForm";
import api from "../../services/api";
import parseMinutesToHourString from "../../utils/parseMinutesToHourString";
import { HoursOfOperation } from "../HoursOfOperation";
import * as Yup from "yup";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SelectForm from "../SelectForm/SelectForm";
import Skeleton from "../Skeleton/Skeleton";
import Button from "../Buttons/Button";
import { arrayMove } from "@dnd-kit/sortable";
import PhotoGallery from "../PhotoGallery";
import "./VenueInformation.scss";

const SectionCard = ({ title, children }) => (
  <div className="venue-section-card">
    <h3 className="venue-section-card__title">{title}</h3>
    <div className="venue-section-card__body">{children}</div>
  </div>
);

const VenueInformation = ({
  specVenue: specVenueProps = {},
  view = false,
  permissions,
  setPermissions,
  showPermissions = false,
}) => {
  const navigate = useNavigate();
  const { venueId } = useParams();
  const [specVenue, setSpecVenue] = useState(specVenueProps);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getVenue = async () => {
      setLoading(true);
      try {
        if (!venueId) {
          setLoading(false);
          return;
        }
        const response = await api.get(`/venues/${venueId}`);
        setSpecVenue(response.data);
        setLoading(false);
      } catch (err) {
        toast.error(`Error retrieving specific venue: ${err}`);
      }
    };
    getVenue();
  }, [venueId]);

  const [additionalHours, setAdditionalHours] = useState([]);
  const [updatedHour, setUpdatedHour] = useState();
  const [funds, setFunds] = useState("");
  const [updatedName, setUpdatedName] = useState("");
  const [updatedAddress, setUpdatedAddress] = useState("");
  const [addressInputValue, setAddressInputValue] = useState("");
  const [updatedUrl, setUpdatedUrl] = useState("");
  const [updatedEmail, setUpdatedEmail] = useState("");
  const [updatedAbout, setUpdatedAbout] = useState("");
  const [updatedPhone, setUpdatedPhone] = useState("");
  const [updatedLat, setUpdatedLat] = useState("");
  const [updatedLong, setUpdatedLong] = useState("");
  const [updatedState, setUpdateState] = useState("");
  const [updatedCountry, setUpdateCountry] = useState("");
  const [updatedCat, setUpdatedCat] = useState([]);
  const [updateSpecials, setUpdateSpecials] = useState("");
  const [updatedManagedBy, setUpdatedManagedBy] = useState("");
  // Partner-site row on this venue's event pages (LIV/Gekko). Loaded values kept to send only what changed.
  const [partnerLabel, setPartnerLabel] = useState("");
  const [partnerNote, setPartnerNote] = useState("");
  const [partnerHidden, setPartnerHidden] = useState(false);
  // Guest check-in (Who's here): missing = off. Admin-only, sent only when changed.
  const [selfCheckIn, setSelfCheckIn] = useState(false);
  const [partnerLoaded, setPartnerLoaded] = useState({ label: "", note: "", hidden: false, selfCheckIn: false });
  const [updatedDress, setUpdatedDress] = useState();
  const [updatedMusic, setUpdatedMusic] = useState([]);
  const [updateCity, setUpdateCity] = useState("");
  const [placeId, setPlaceId] = useState();
  const [images, setImages] = useState([]);
  const imagesHydratedRef = useRef(false);
  const [errors, setErrors] = useState({});

  const validationSchema = Yup.object().shape({});

  const handleEditVenueSubmit = async (event) => {
    event.preventDefault();
    const parsedHours = additionalHours.reduce((acc, cur) => {
      const { day, hours } = cur;
      acc[day] = hours;
      return acc;
    }, {});
    if (updatedHour) parsedHours[updatedHour.day] = updatedHour.hours;

    try {
      await validationSchema.validate({ funds }, { abortEarly: false });
      const updatedVenue = {
        name: updatedName,
        tags: updatedCat,
        address: updatedAddress,
        hours: parsedHours,
        url: updatedUrl,
        email: updatedEmail,
        phone: updatedPhone,
        lat: updatedLat,
        lng: updatedLong,
        dress: updatedDress,
        music: updatedMusic,
        description: updatedAbout,
        city: updateCity,
        state: updatedState,
        country: updatedCountry,
        specials: updateSpecials,
        managed_by: updatedManagedBy.trim(),
        ...(partnerLabel.trim() !== partnerLoaded.label ? { partner_button_label: partnerLabel.trim() } : {}),
        ...(partnerNote.trim() !== partnerLoaded.note ? { partner_note: partnerNote.trim() } : {}),
        ...(partnerHidden !== partnerLoaded.hidden ? { partner_row_hidden: partnerHidden } : {}),
        ...(user?.admin && !view && selfCheckIn !== partnerLoaded.selfCheckIn ? { self_check_in_enabled: selfCheckIn } : {}),
        place_id: placeId,
        ...(showPermissions ? { permissions } : {}),
      };

      if (user?.admin) {
        updatedVenue.funds = parseFloat(funds);
      }

      try {
        await api.put(`/venues/${specVenue._id}`, updatedVenue);

        toast.success("Business updated successfully.");
        navigate("/business");
      } catch (err) {
        console.error(`Error updating venue: ${err}`);
        toast.error("Error updating venue. Please try again.");
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
        toast.error(`Error editing venue: ${error.message}`);
      } else {
        console.error(error);
      }
    }
  };

  const days = useMemo(() => [
    "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
  ], []);

  useEffect(() => {
    setUpdatedName(specVenue.name || "");
    setUpdatedLong(specVenue.lng || "");
    setUpdatedLat(specVenue.lat || "");
    setUpdatedUrl(specVenue.url || specVenue.website || "");
    setUpdatedPhone(specVenue.phone || "");
    setUpdatedEmail(specVenue.email || "");
    setUpdatedDress(specVenue.dress || "");
    setUpdatedMusic(specVenue.music || []);
    setUpdatedAbout(specVenue.description || "");
    setUpdatedCat(specVenue.category || specVenue.tags || []);
    setUpdatedAddress(specVenue.address || "");
    setAddressInputValue(specVenue.address || "");
    setUpdateCity(specVenue.city || "");
    setUpdateSpecials(specVenue.specials || "");
    setUpdatedManagedBy(specVenue.managed_by || "");
    setPartnerLabel(specVenue.partner_button_label || "");
    setPartnerNote(specVenue.partner_note || "");
    setPartnerHidden(!!specVenue.partner_row_hidden);
    const loadedSelfCheckIn = specVenue.self_check_in_enabled === true;
    setPartnerLoaded({
      label: specVenue.partner_button_label || "",
      note: specVenue.partner_note || "",
      hidden: !!specVenue.partner_row_hidden,
      selfCheckIn: loadedSelfCheckIn,
    });
    setSelfCheckIn(loadedSelfCheckIn);

    if (specVenue?.image?.length && !imagesHydratedRef.current) {
      const imgs = specVenue.image.map((img, index) => ({
        id: img,
        url: specVenue.image_urls[index],
        status: "confirmed",
      }));
      setImages(imgs);
      imagesHydratedRef.current = true;
    }

    const parsedHours = Object.entries(specVenue.hours || {}).reduce(
      (acc, [key, value]) => {
        acc.push({ day: key, hours: value });
        return acc;
      },
      []
    );

    const sortedHours = parsedHours.sort((a, b) => days.indexOf(a.day) - days.indexOf(b.day));
    setUpdatedHour(sortedHours[0]);
    setAdditionalHours(sortedHours.slice(1));
  }, [specVenue, days]);

  const handleCategoryChange = (selectedOptions) => {
    const selectedValues = Array.isArray(selectedOptions)
      ? selectedOptions.map((option) => option.value)
      : [];
    setUpdatedCat(selectedValues);
  };

  const handleDressChange = (selectedOptions) => {
    setUpdatedDress(selectedOptions ? selectedOptions.value : "");
  };

  const allPermissions = ["all", "guestlist", "bottleService", "ticketSales", "offers", "withdrawalRequests", "followers"];

  const handlePermissionChange = (event) => {
    const { name, checked } = event.target;
    setPermissions((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      if (name === "all") {
        return checked ? ["all"] : [];
      } else {
        let newPermissions = safePrev.filter((perm) => perm !== "all");
        newPermissions = checked
          ? [...newPermissions, name]
          : newPermissions.filter((perm) => perm !== name);
        return newPermissions;
      }
    });
  };

  const handleMusicChange = (selectedOption) => {
    const selectValues = Array.isArray(selectedOption)
      ? selectedOption.map((option) => option.value)
      : [];
    setUpdatedMusic(selectValues);
  };

  const handleSelect = async (result) => {
    try {
      const placeDetails = await geocodeByPlaceId(result.value.place_id);
      const {
        address_components,
        formatted_address,
        geometry: { location: { lat, lng } },
      } = placeDetails[0];

      const city = address_components.find((address) =>
        address.types.some((type) => type === "administrative_area_level_2")
      );
      const state = address_components.find((address) =>
        address.types.some((type) => type === "administrative_area_level_1")
      );
      const country = address_components.find((address) =>
        address.types.some((type) => type === "country")
      );

      if (city) setUpdateCity(city.long_name);
      if (state) setUpdateState(state.short_name);
      if (country) setUpdateCountry(country.short_name);

      setUpdatedAddress(formatted_address);
      setAddressInputValue(formatted_address);
      setUpdatedLat(lat);
      setUpdatedLong(lng);
      setPlaceId(result.value.place_id);
    } catch (error) {
      console.error("Error in handleSelect:", error);
    }
  };

  const omitDays = useMemo(() => {
    const d = additionalHours.reduce((acc, cur) => {
      if (cur.day) acc.push(cur.day);
      return acc;
    }, []);
    if (updatedHour?.day) d.push(updatedHour.day);
    return d;
  }, [updatedHour, additionalHours]);

  const handleOnDayChange = (index, event) => {
    const { value } = event;
    setAdditionalHours((oldState) => {
      const updatedState = [...oldState];
      const { hours } = updatedState[index];
      updatedState[index] = { day: value, hours };
      return updatedState;
    });
  };

  const handleAddHours = () => {
    setAdditionalHours((oldState) => [...oldState, { hours: {}, day: "" }]);
  };

  const handleRemoveHours = (index) => {
    setAdditionalHours((oldState) => {
      const newState = [...oldState];
      newState.splice(index, 1);
      return newState;
    });
  };

  const handleOnTimeChange = (index, event) => {
    const { value, type } = event;
    setAdditionalHours((oldState) => {
      const updatedState = [...oldState];
      const { hours, day } = updatedState[index];
      updatedState[index] = { day, hours: { ...hours, [type]: value } };
      return updatedState;
    });
  };

  const defaultHourValue = useMemo(() => {
    if (!updatedHour) return undefined;
    return {
      day: updatedHour.day,
      hours: {
        from: updatedHour?.hours?.from
          ? parseMinutesToHourString(updatedHour.hours.from)
          : "00:00",
        to: updatedHour?.hours?.to
          ? parseMinutesToHourString(updatedHour.hours.to)
          : "00:00",
      },
    };
  }, [updatedHour]);

  const uploadOne = useCallback(async (tempId, file) => {
    if (!specVenue?._id) return;
    try {
      const formData = new FormData();
      formData.append("images", file);
      const { data } = await api.post(`/venues/images/${specVenue._id}`, formData);
      const serverPath = data[0];
      setImages((prev) => prev.map((p) =>
        p.id === tempId ? { ...p, id: serverPath, status: "confirmed" } : p
      ));
    } catch (err) {
      console.error("Upload failed:", err);
      setImages((prev) => prev.map((p) => (p.id === tempId ? { ...p, status: "error" } : p)));
      toast.error("Photo upload failed");
    }
  }, [specVenue]);

  const handleFilesPicked = useCallback((files) => {
    if (!specVenue?._id) {
      toast.error("Save the venue first before adding photos");
      return;
    }
    const list = Array.from(files).filter((f) => f.type?.startsWith("image/"));
    if (!list.length) return;
    const newPhotos = list.map((file) => ({
      id: `temp-${uuidv4()}`,
      url: URL.createObjectURL(file),
      status: "uploading",
      file,
    }));
    setImages((prev) => [...prev, ...newPhotos]);
    newPhotos.forEach((p) => uploadOne(p.id, p.file));
  }, [specVenue, uploadOne]);

  const handleRetry = useCallback((id) => {
    const photo = images.find((p) => p.id === id);
    if (!photo?.file) return;
    setImages((prev) => prev.map((p) => (p.id === id ? { ...p, status: "uploading" } : p)));
    uploadOne(id, photo.file);
  }, [images, uploadOne]);

  const handleImageRemove = useCallback(async (id) => {
    if (!specVenue?._id) return;
    const target = images.find((p) => p.id === id);
    if (!target) return;
    if (!window.confirm("Delete this photo?")) return;

    if (target.status !== "confirmed") {
      if (target.url?.startsWith("blob:")) URL.revokeObjectURL(target.url);
      setImages((prev) => prev.filter((p) => p.id !== id));
      return;
    }

    const previous = images;
    setImages((prev) => prev.filter((p) => p.id !== id));
    try {
      await api.delete(`/venues/images/${specVenue._id}`, { params: { images: [id] } });
      toast.success("Photo deleted");
    } catch (err) {
      console.error('Error deleting image:', err);
      toast.error("Could not delete photo");
      setImages(previous);
    }
  }, [specVenue, images]);

  const persistImageOrder = useCallback(async (orderedIds) => {
    if (!specVenue?._id) return;
    try {
      await api.put(`/venues/images/${specVenue._id}/order`, { image: orderedIds });
    } catch (err) {
      console.error("Error reordering venue images:", err);
      toast.error("Could not save photo order");
      throw err;
    }
  }, [specVenue]);

  const handleReorder = useCallback((next) => {
    const previous = images;
    setImages(next);
    const confirmedIds = next.filter((p) => p.status === "confirmed").map((p) => p.id);
    persistImageOrder(confirmedIds).catch(() => setImages(previous));
  }, [images, persistImageOrder]);

  const handleSetMain = useCallback((id) => {
    const confirmed = images.filter((p) => p.status === "confirmed");
    const pending = images.filter((p) => p.status !== "confirmed");
    const currentIndex = confirmed.findIndex((p) => p.id === id);
    if (currentIndex <= 0) return;
    const nextConfirmed = arrayMove(confirmed, currentIndex, 0);
    const previous = images;
    setImages([...nextConfirmed, ...pending]);
    persistImageOrder(nextConfirmed.map((p) => p.id)).catch(() => setImages(previous));
  }, [images, persistImageOrder]);

  const handleDiscard = useCallback((id) => {
    setImages((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.url?.startsWith("blob:")) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const googlePlacesTypesToFetch = ["establishment"];

  useEffect(() => {
    if (!updatedAddress && specVenue.address) {
      setUpdatedAddress(specVenue.address);
      setAddressInputValue(specVenue.address);
    }
  }, [updatedAddress, specVenue.address]);

  const [filters, setFilters] = useState();
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [dressCodeOptions, setDressCodeOptions] = useState([]);
  const [musicOptions, setMusicOptions] = useState([]);

  useEffect(() => {
    async function getFilters() {
      try {
        const response = await api.get('/filters', { params: { includeAll: true } });
        const { tags, dress_code, music } = response.data;
        setCategoryOptions(tags.map((tag) => ({ value: tag, label: tag })));
        setDressCodeOptions(dress_code.map((tag) => ({ value: tag, label: tag })));
        setMusicOptions(music.map((tag) => ({ value: tag, label: tag })));
        setFilters(response.data);
      } catch (err) {
        console.error(err);
      }
    }
    getFilters();
  }, []);

  const [user, setUser] = useState();

  useEffect(() => {
    async function loadUser() {
      const { uid } = auth?.currentUser;
      const { data } = await api.get(`/users/${uid}`);
      setUser(data.user);
    }
    loadUser();
  }, []);

  return (
    <article className="venue-info">
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Skeleton type="row" /><Skeleton type="row" /><Skeleton type="row" /><Skeleton type="row" />
        </div>
      ) : (
        <form onSubmit={handleEditVenueSubmit} className="venue-info__form">

          {/* ── Two-column layout: Left (info) + Right (hours) ── */}
          <div className="venue-two-col">
            <div className="venue-two-col__left">
              {/* ── Basic Info ── */}
              <SectionCard title="Basic Info">
                <div className="row g-3">
                  <div className="col-12">
                    <InputForm
                      label="Name"
                      type="text"
                      id="venueName"
                      name="name"
                      value={updatedName}
                      onChange={(event) => setUpdatedName(event.target.value)}
                      error={errors.updatedName}
                    />
                  </div>
                  <div className="col-md-6">
                    <InputForm
                      label="Phone"
                      type="text"
                      id="venuePhone"
                      name="phone"
                      value={updatedPhone}
                      onChange={(event) => setUpdatedPhone(event.target.value)}
                      placeholder="Enter phone"
                      error={errors.updatedPhone}
                    />
                  </div>
                  <div className="col-md-6">
                    <InputForm
                      label="E-mail"
                      type="email"
                      id="venueEmail"
                      name="email"
                      value={updatedEmail}
                      onChange={(event) => setUpdatedEmail(event.target.value)}
                      placeholder="Enter e-mail"
                      error={errors.updatedEmail}
                    />
                  </div>
                  <div className="col-12">
                    <InputForm
                      label="Website"
                      type="text"
                      id="venueSite"
                      name="url"
                      value={updatedUrl}
                      onChange={(event) => setUpdatedUrl(event.target.value)}
                      placeholder="Enter website"
                      error={errors.updatedUrl}
                    />
                  </div>
                  <div className="col-12">
                    {/* Shown on the app's venue page as "Managed by <name>". A name only, no account. */}
                    <InputForm
                      label="Managed by"
                      type="text"
                      id="venueManagedBy"
                      name="managed_by"
                      value={updatedManagedBy}
                      onChange={(event) => setUpdatedManagedBy(event.target.value.slice(0, 80))}
                      placeholder="Name of the person who runs the room"
                    />
                  </div>
                  <div className="col-12">
                    {/* Event pages with a partner link: the wide button's text. Empty uses the app's ON PARTNER SITE. */}
                    <InputForm
                      label="Partner site button"
                      type="text"
                      id="venuePartnerLabel"
                      name="partner_button_label"
                      value={partnerLabel}
                      onChange={(event) => setPartnerLabel(event.target.value.slice(0, 40))}
                      placeholder="ON PARTNER SITE"
                    />
                  </div>
                  <div className="col-12">
                    {/* The line under that button saying what's on the partner site. Empty shows no line. */}
                    <InputForm
                      label="Partner site note"
                      type="text"
                      id="venuePartnerNote"
                      name="partner_note"
                      value={partnerNote}
                      onChange={(event) => setPartnerNote(event.target.value.slice(0, 80))}
                      placeholder="* Bottle Service and Tickets"
                    />
                  </div>
                  <div className="col-12">
                    <label className="venue-permission-item" style={{ padding: "0 16px" }}>
                      <input
                        type="checkbox"
                        id="venuePartnerHidden"
                        name="partner_row_hidden"
                        checked={partnerHidden}
                        onChange={(event) => setPartnerHidden(event.target.checked)}
                        style={{ accentColor: "#6e34ff" }}
                      />
                      Hide the partner site button
                    </label>
                  </div>
                  {user?.admin && (
                    <div className="col-12">
                      <label htmlFor="venueSelfCheckIn" className="venue-permission-item" style={{ padding: "0 16px" }}>
                        <input
                          type="checkbox"
                          role="switch"
                          id="venueSelfCheckIn"
                          name="self_check_in_enabled"
                          checked={selfCheckIn}
                          onChange={(event) => setSelfCheckIn(event.target.checked)}
                          disabled={view}
                          aria-describedby="venueSelfCheckInHelp"
                          style={{ accentColor: "#6e34ff" }}
                        />
                        Guest check-in
                      </label>
                      <div id="venueSelfCheckInHelp" style={{ padding: "0 16px", fontSize: 12, color: "rgba(255,255,255,.45)", marginTop: 3 }}>
                        Guests can check in at the venue and see who else is here tonight.
                      </div>
                    </div>
                  )}
                </div>
              </SectionCard>

              {/* ── Location ── */}
              <SectionCard title="Location">
                <label style={{ padding: "0 16px 8px", color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
                  Address
                  <span className="error-message">{errors.updatedAddress}</span>
                </label>
                <PlacesAutocomplete
                  autocompletionRequest={{ types: googlePlacesTypesToFetch }}
                  selectProps={{
                    onChange: handleSelect,
                    placeholder: updatedAddress,
                    styles: {
                      control: (provided) => ({
                        ...provided,
                        background: "rgba(255, 255, 255, 0.06)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: 10,
                        padding: "4px 16px",
                      }),
                      input: (provided) => ({ ...provided, color: "#fff" }),
                      menuList: (provided) => ({ ...provided, color: "black" }),
                      singleValue: (provided) => ({ ...provided, color: "#fff" }),
                      valueContainer: (provided) => ({ ...provided, padding: "0" }),
                    },
                    onInputChange: (val) => setAddressInputValue(val),
                    inputValue: addressInputValue,
                  }}
                />
              </SectionCard>

              {/* ── Style & Vibe ── */}
              <SectionCard title="Style & Vibe">
                <div className="row g-3">
                  <div className="col-12">
                    <SelectForm
                      label="Dress Code"
                      name="dress"
                      options={dressCodeOptions}
                      className="venue-info__form-group-field-select"
                      value={dressCodeOptions.filter((option) => updatedDress?.includes(option.value))}
                      onChange={handleDressChange}
                      getOptionValue={(option) => option.value}
                      error={errors.updatedDress}
                    />
                  </div>
                  <div className="col-12">
                    <SelectForm
                      label="Music"
                      isMulti
                      options={musicOptions}
                      className="venue-info__form-group-field-select"
                      value={musicOptions.filter((option) => updatedMusic.includes(option.value))}
                      onChange={handleMusicChange}
                      error={errors.updatedMusic}
                    />
                  </div>
                </div>
              </SectionCard>

              {/* ── Description ── */}
              <SectionCard title="Description">
                <div className="row g-3">
                  <div className="col-12">
                    <label htmlFor="venueDescription" className="venue-info__form-group-field-label">
                      About
                      <span className="error-message">{errors.updatedAbout}</span>
                    </label>
                    <textarea
                      rows={3}
                      id="venueDescription"
                      name="description"
                      className="venue-info__form-group-about-input w-100"
                      value={updatedAbout}
                      onChange={(event) => setUpdatedAbout(event.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label htmlFor="venueAbout" className="venue-info__form-group-field-label">
                      More Information (Specials)
                      <span className="error-message">{errors.updateSpecials}</span>
                    </label>
                    <textarea
                      placeholder="Description of the venue"
                      rows={3}
                      id="venueAbout"
                      name="specials"
                      className="venue-info__form-group-about-input w-100"
                      value={updateSpecials}
                      onChange={(event) => setUpdateSpecials(event.target.value)}
                    />
                  </div>
                </div>
              </SectionCard>
            </div>

            <div className="venue-two-col__right">
              {/* ── Hours of Operation ── */}
              <SectionCard title="Hours of Operation">
                <div className="venue-hours-list">
                  <div className="venue-hours-row">
                    <HoursOfOperation
                      excludeDays={omitDays}
                      defaultValues={defaultHourValue}
                      onChangeDay={({ value }) =>
                        setUpdatedHour((oldState) =>
                          !oldState?.hours ? { day: value } : { ...oldState, day: value }
                        )
                      }
                      onChangeTime={({ value, type }) =>
                        setUpdatedHour((oldState) =>
                          !oldState?.day
                            ? { hours: { [type]: value } }
                            : { day: oldState.day, hours: { ...oldState.hours, [type]: value } }
                        )
                      }
                    />
                  </div>
                  {additionalHours.map((item, index) => {
                    const defaultValues = item
                      ? {
                        day: item.day,
                        hours: {
                          from: parseMinutesToHourString(item.hours.from),
                          to: parseMinutesToHourString(item.hours.to),
                        },
                      }
                      : {};
                    return (
                      <div className="venue-hours-row" key={index}>
                        <HoursOfOperation
                          excludeDays={omitDays}
                          onChangeDay={(event) => handleOnDayChange(index, event)}
                          onChangeTime={(event) => handleOnTimeChange(index, event)}
                          defaultValues={defaultValues}
                        />
                        <button type="button" onClick={() => handleRemoveHours(index)} className="venue-hours-remove-btn">-</button>
                      </div>
                    );
                  })}
                </div>
                {additionalHours.length < 6 && (
                  <button type="button" onClick={handleAddHours} className="venue-hours-add-btn" style={{ marginTop: 8 }}>+</button>
                )}
              </SectionCard>

              {/* ── Media ── */}
              <SectionCard title="Media">
                <PhotoGallery
                  photos={images}
                  onFilesPicked={handleFilesPicked}
                  onReorder={handleReorder}
                  onSetMain={handleSetMain}
                  onRemove={handleImageRemove}
                  onRetry={handleRetry}
                  onDiscard={handleDiscard}
                />
              </SectionCard>

              {/* ── Category ── */}
              <SectionCard title="Category">
                <SelectForm
                  label=""
                  isMulti
                  options={categoryOptions}
                  className="venue-info__form-group-field-select"
                  value={categoryOptions.filter((option) => updatedCat.includes(option.value))}
                  onChange={handleCategoryChange}
                  error={errors.updatedCat}
                />
              </SectionCard>
            </div>
          </div>

          {/* ── Permissions (conditional) ── */}
          {showPermissions && (
            <SectionCard title="Zona Portal Permissions">
              <div className="venue-permissions-grid">
                {allPermissions.map((perm) => (
                  <label key={perm} className="venue-permission-item">
                    <input
                      type="checkbox"
                      name={perm}
                      checked={permissions?.includes(perm) || false}
                      onChange={handlePermissionChange}
                      style={{ accentColor: "#6e34ff" }}
                    />
                    {perm.charAt(0).toUpperCase() + perm.slice(1)}
                  </label>
                ))}
              </div>
            </SectionCard>
          )}


          {/* ── Sticky Save ── */}
          {!view && (
            <div className="venue-sticky-save">
              <Button
                label="Save Changes"
                type="submit"
                iconifyIconName="material-symbols:check-rounded"
                primaryBtn
              />
            </div>
          )}
        </form>
      )}
    </article>
  );
};

export default VenueInformation;
